import {
  CastorConfigService,
  DetailRepository,
  MultiImageDownloadRepository,
  SearchRepository,
  SearchService
} from '@aipb/gulliver/catalog/data-access';
import { CatalogEnvService } from '@aipb/gulliver/catalog/env';
import { MultiImageDownloadComponent } from '@aipb/gulliver/catalog/feature-multi-image-download';
import { DetailItem, SearchItem } from '@aipb/gulliver/catalog/model';
import { CatalogRouting } from '@aipb/gulliver/catalog/routing';
import { createDetailCompositeKey } from '@aipb/gulliver/catalog/util';
import { EnvService } from '@aipb/gulliver/env/util';
import { FrameRepository } from '@aipb/gulliver/frame/data-access';
import { FavoriteLibService, FavoriteNibbleService } from '@aipb/gulliver/personal-content/data-access';
import { AuthService } from '@aipb/shared/auth';
import { CommonModule } from '@angular/common';
import { ApplicationRef, Component, OnInit } from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest } from 'rxjs';
import { filter, mergeMap, take, tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'cat-entry',
  templateUrl: './entry.component.html',
  styleUrls: ['./entry.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterOutlet, MatSidenavModule, MultiImageDownloadComponent]
})
export class EntryComponent implements OnInit {
  multiImageDownloadActive$ = this.multiImageDownloadRepository.active$;
  forceFullHeight$ = this.frameRepository.forceFullHeight$;

  constructor(
    private catalogEnv: CatalogEnvService,
    private searchRepository: SearchRepository,
    transloco: TranslocoService,
    appRef: ApplicationRef,
    private catalogRouting: CatalogRouting,
    private detailRepository: DetailRepository,
    private favoriteLibService: FavoriteLibService,
    private searchService: SearchService,
    private castorConfigService: CastorConfigService,
    private frameRepository: FrameRepository,
    private route: ActivatedRoute,
    private favoriteNibbleService: FavoriteNibbleService,
    private multiImageDownloadRepository: MultiImageDownloadRepository,
    private auth: AuthService,
    private env: EnvService
  ) {
    frameRepository.isMobile$.pipe(untilDestroyed(this)).subscribe((isMobile) => {
      if (isMobile) {
        this.searchService.hideFacetList();
      }
    });

    this.env.frame.languages.forEach((lang) => {
      transloco.setTranslation(
        {
          catalog: {
            titles: catalogEnv.env[lang.name]
          }
        },
        lang.name,
        {
          merge: true
        }
      );
    });

    transloco.langChanges$.pipe(untilDestroyed(this)).subscribe((lang) => {
      this.castorConfigService.load();
      appRef.tick();
    });
  }

  ngOnInit(): void {
    this.searchRepository.tabs$
      .pipe(
        take(1),
        filter((items) => items.length === 0 && this.catalogEnv.env.enableFirstSearch),
        tap(() => {
          this.searchService.addTab({
            solrSearchParams: {
              query: '*:*'
            }
          });
        }),
        untilDestroyed(this)
      )
      .subscribe();

    this.route.queryParams.subscribe((params) => {
      if (params.q && params.coordinates) {
        this.searchService.addTab(
          {
            coordinates: params.coordinates.split(',').map(Number),
            solrSearchParams: {
              query: params.q,
              coreId: params?.coreId,
              spatialFilter: {
                spatialField: 'Coordinates',
                spatialType: 'rectangle',
                rectangle: params.coordinates
              }
            },
            settings: {}
          },
          true
        );
        this.catalogRouting.searchResult();
        return;
      }

      if (params.q) {
        this.searchService.addTab(
          {
            solrSearchParams: {
              query: params.q,
              coreId: params?.coreId
            },
            settings: {}
          },
          true
        );
        this.catalogRouting.searchResult();
        return;
      }

      if (params.qf && params.qs) {
        this.searchService.addTab(
          {
            solrSearchParams: {
              coreId: params?.coreId,
              query: `${params.qf}:${params.qs}`
            },
            settings: {}
          },
          true
        );
        this.catalogRouting.searchResult();
      }
    });

    if (!this.env.isPersonalContentEnabled) return;

    this.favoriteNibbleService.deletedFromCollection$
      .pipe(mergeMap(() => combineLatest([this.searchRepository.items$, this.detailRepository.items$])))
      .subscribe((data) => {
        this.loadSearchFavorites(data[0]);
        this.loadDetailFavorites(data[1]);
      });

    this.searchRepository.items$.pipe(untilDestroyed(this)).subscribe((data) => {
      this.loadSearchFavorites(data);
    });

    this.detailRepository.items$.pipe(untilDestroyed(this)).subscribe((data) => {
      this.loadDetailFavorites(data);
    });
  }

  private loadSearchFavorites(data: SearchItem[]): void {
    const ids: string[] = [];

    for (const d of data) {
      for (const record of d.search.recordList) {
        ids.push(createDetailCompositeKey(d.coreId, record[this.catalogEnv.env.castor.UUID]));
      }
    }

    this.loadFavorites(ids);
  }

  private loadDetailFavorites(data: DetailItem[]): void {
    const ids: string[] = [];

    for (const d of data) {
      ids.push(createDetailCompositeKey(d.coreId, d.document[this.catalogEnv.env.castor.UUID]));
    }

    this.loadFavorites(ids);
  }

  private loadFavorites(ids: string[]) {
    if (ids.length === 0) return;

    this.favoriteLibService.getFavorites(this.catalogEnv.env.service, ids).subscribe();
  }
}
